import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook that delays showing loading state until a minimum time has passed
 * If the operation completes before the delay, loading state is never shown
 * @param isLoading - The actual loading state from the operation
 * @param delay - Delay in milliseconds before showing loading state (default: 500ms)
 * @returns boolean - Whether to show loading state to user
 */
export const useDebouncedLoading = (isLoading: boolean, delay: number = 500): boolean => {
  const [showLoading, setShowLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isLoading) {
      // Start a timer when loading begins
      timeoutRef.current = setTimeout(() => {
        setShowLoading(true);
      }, delay);
    } else {
      // If loading finishes before delay, clear the timer and don't show loading
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setShowLoading(false);
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isLoading, delay]);

  return showLoading;
};

